/// <amd-module name="Wit/WorkItemDataManager/Sources/WorkItemStoreLevelWITDataSource" />
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPerformanceService, IVssScenario } from "VSS/Platform/Performance";
import { traceWarning } from "VSS/Platform/Trace";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { WITCommonConstants } from "Wit/Common/Generated/Constants";
import { WorkItemIndexDBCacheManager } from "Wit/WorkItemDataManager/CacheManagers/WorkItemIndexDBCacheManager";
import { IDataProviderResponse, IDataSourceResponse, IRouteParams } from "Wit/WorkItemDataManager/Contracts";
import * as Resources from "Wit/WorkItemDataManager/Resources";

const MVCActionToDataproviderIdMapping: { [key: string]: string } = {
    fields: "ms.vss-work-web.fields-data-provider",
    nwpFields: "ms.vss-work-web.nwp-fields-data-provider",
    allowedValues: "ms.vss-work-web.fields-allowed-values-provider",
    ResourceLinks: "ms.vss-work-web.resource-links-data-provider",
    workItemTypeExtensions: "ms.vss-work-web.work-item-type-extensions-data-provider",
    updateWorkItems: "ms.vss-work-web.update-work-items-data-provider",
    constantSets: "ms.vss-work-web.constant-sets-data-provider",
    getMetadataCacheStamps: "ms.vss-work-web.work-item-cache-data-provider",
    linkTypes: "ms.vss-work-web.work-item-link-types-data-provider",
    teamProjects: "ms.vss-work-web.work-item-team-projects-data-provider",
    workItemTypes: "ms.vss-work-web.work-item-types-data-provider",
    pageWorkItems: "ms.vss-work-web.page-work-items-data-provider",
    pageWorkItemsByIdRev: "ms.vss-work-web.page-work-items-by-id-rev-data-provider",
    AdHocQueries: "ms.vss-work-web.ad-hoc-queries-data-provider",
    query: "ms.vss-work-web.work-item-query-data-provider",
    updateAdHocQuery: "ms.vss-work-web.update-ad-hoc-query-data-provider",
    updateColumnOptions: "ms.vss-work-web.update-column-options-data-provider",
    search: "ms.vss-work-web.search-data-provider",
    queryFavorites: "ms.vss-work-web.query-favorites-data-provider",
    teamSettings: "ms.vss-work-web.team-settings-data-provider",
    workitems: "ms.vss-work-web.work-items-with-history-data-provider",
    workitemsnohistory: "ms.vss-work-web.work-items-no-history-data-provider",
    sendMail: "ms.vss-work-web.send-mail-data-provider",
    workitemcolor: "ms.vss-work-web.work-item-color-data-provider",
    nodes: "ms.vss-work-web.classification-nodes-data-provider",
};

export class WorkItemStoreLevelWITDataSource {
    private readonly _workItemCacheManager: WorkItemIndexDBCacheManager;
    public _pageContext: IVssPageContext;
    private useFieldsDataProviderFiltering: boolean = false;
    private useNewWorkItemFieldService: boolean = false;

    constructor(pageContext: IVssPageContext) {
        this._workItemCacheManager = new WorkItemIndexDBCacheManager(pageContext);
        this._pageContext = pageContext;
        this.useFieldsDataProviderFiltering = isFeatureFlagEnabled(
            this._pageContext,
            FeatureFlags.UseFieldsDataProviderFiltering,
            false
        );

        this.useNewWorkItemFieldService = isFeatureFlagEnabled(
            this._pageContext,
            FeatureFlags.UseNewWorkItemFieldService,
            false
        );

    }

    /**
     * Attempts to get the data from the cache. If that fails, we get the data from a data provider then set the cache to the
     * data provider response.
     * @param routeParams The route parameters
     * @param requestProperties properties for the cache access or data provider access
     * @param scopeId scope of the cache (Project, org, etc)
     * @param type type of data to request from the cache
     * @param key the key of the data in the cache
     * @returns
     */
    public getDataFromCacheAsync<T>(
        routeParams: IRouteParams,
        requestProperties: any,
        scopeId: string,
        type: string,
        key: string
    ): Promise<IDataSourceResponse<T>> {
        return this.getDataProviderDataUsingIndexDB<T>(routeParams, requestProperties, scopeId, type, key).then(({ resolvedFromCache, data }) => {
            if (data && !resolvedFromCache) {
                const dataFromProvider: IDataSourceResponse<T> = data as IDataSourceResponse<T>;
                return this.updateIndexDBCache<T>(dataFromProvider, scopeId, type, key).then(() => dataFromProvider);
            }

            return data;
        });
    }

    public getDataWithoutCache<T>(routeParams: IRouteParams, requestProperties: any): Promise<IDataSourceResponse<T>> {
        return this.getDataFromDataProviderAsync<T>(routeParams, requestProperties, this.getDataProviderId(routeParams));
    }

    /**
     * Attempts to get the data from the cache. If that fails, we get the data from a data provider
     * @param routeParams The route parameters
     * @param requestProperties properties for the cache access or data provider access
     * @param scopeId scope of the cache (Project, org, etc)
     * @param type type of data to request from the cache
     * @param key the key of the data in the cache
     * @returns
     */
    public getDataProviderDataUsingIndexDB<T>(
        routeParams: IRouteParams,
        requestProperties: any,
        scopeId: string,
        type: string,
        key: string
    ): Promise<{ resolvedFromCache: boolean; data: IDataSourceResponse<T> }> {
        var requestPropertiesStamp = requestProperties != null ? requestProperties.stamp : null;

        // First try to get data from cache if not present then get it from source.
        const performanceService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario: IVssScenario = performanceService.startScenario("WIT-ds-data-provider-with-indexDB", false);
        perfScenario.addProperties({
            routeParams: routeParams,
            requestProperties: requestProperties,
            scopeId: scopeId,
            type: type,
            key: key
        });

        return this._workItemCacheManager.getDataFromCache<T>(scopeId, type, key, requestPropertiesStamp).then(dataFromCache => {
            if (!dataFromCache) {
                perfScenario.addProperties({
                    cacheHit: false
                });
                // Try get data from data sources
                return this.getDataFromDataProviderAsync<T>(routeParams, requestProperties, this.getDataProviderId(routeParams)).then(
                    (dataFromProvider: IDataSourceResponse<T>) => {
                        performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
                        return {
                            resolvedFromCache: false,
                            data: dataFromProvider
                        };
                    }
                );
            }
            perfScenario.addProperties({
                cacheHit: true
            });
            performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);
            const cachedDataInDataProviderResponseFormat: IDataSourceResponse<T> = { data: dataFromCache, versionStamp: requestPropertiesStamp };
            return {
                resolvedFromCache: true,
                data: cachedDataInDataProviderResponseFormat
            };
        });
    }

    /**
     * Caches the data returned from a dataProvider async call
     * @param dataFromSource Response of the data and stamp to cache
     * @param scopeId scope of the cache (Project, org, etc)
     * @param type type of data to request from the cache
     * @param key the key of the data in the cache
     * @returns Promise of the caching operation
     */
    public updateIndexDBCache<T>(dataFromSource: IDataSourceResponse<T>, scopeId: string, type: string, key: string): Promise<string[] | null> {
        // We retrieved data, update the cache if a version stamp is passed as part of the data, and ensure we wait until we hand the data back.
        if (dataFromSource && dataFromSource.versionStamp && dataFromSource.versionStamp.length > 0) {
            // PerfFix: Do not wait for cache write to complete, since it is an expensive I/O operation
            return this._workItemCacheManager.cacheData(scopeId, type, key, dataFromSource.data, dataFromSource.versionStamp).catch(reason => {
                // Do not fail when caching fails
                traceWarning(this._pageContext, WITCommonConstants.WorkItemTrackingArea, "updateIndexDBCache", reason);
                return null;
            });
        }

        return Promise.resolve(null);
    }

    /**
     *
     * @param routeParams The routeParams of the request. Action of the routeParams is used as a key in the Id map
     * @returns
     */
    private getDataProviderId(routeParams: IRouteParams): string {
        if (routeParams && routeParams.action && MVCActionToDataproviderIdMapping[routeParams.action]) {
            if (equals(routeParams.action, "fields", true)
                && this.useFieldsDataProviderFiltering
                && this.useNewWorkItemFieldService) {
                return MVCActionToDataproviderIdMapping["nwpFields"];
            }

            return MVCActionToDataproviderIdMapping[routeParams.action];
        }
        throw new Error(format(Resources.MVCActionToDpIdMappingNotFoundError, routeParams.action));
    }

    /**
     * Uses the contribution service to call getDataAsync then processes the data with a version stamp
     * @param routeParams The route parameter
     * @param requestProperties The resuet properties used in getDataAsync()
     * @param dataProviderId The data provider to request the data from
     * @returns Data in the form of IDataSourceResponse
     */
    private async getDataFromDataProviderAsync<T>(
        routeParams: IRouteParams,
        requestProperties: any,
        dataProviderId: string
    ): Promise<IDataSourceResponse<T>> {
        const performanceService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const perfScenario: IVssScenario = performanceService.startScenario("WIT-ds-data-provider-async", false);

        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        const result = await contributionService.getDataAsync<IDataProviderResponse>(dataProviderId, requestProperties, true);

        performanceService.endScenario(WITCommonConstants.WorkItemTrackingArea, perfScenario.scenarioName, false);

        if (!result) {
            const dpExceptions = contributionService.getDataProviderExceptions();
            const exception = dpExceptions[dataProviderId];
            if (exception) {
                const error = new Error(exception.message);
                (error as any).exceptionType = exception.exceptionType;
                (error as any).serverStackTrace = exception.stackTrace
                throw error;
            }
            throw result;
        } else if (result.errorMessage && result.errorMessage.length > 0) {
            throw result.errorMessage;
        } else {
            const witDataSourceResponse: IDataSourceResponse<T> = { data: result.data, versionStamp: result.versionStamp };
            return witDataSourceResponse;
        }
    }
}


